import type {
  FinanceiroExtratoContaDay,
  FinanceiroExtratoContaFile,
  FinanceiroExtratoContaMonth,
  FinanceiroExtratoContaScopeRow,
  FinanceiroExtratoContaTree,
} from '../types/financeiroExtratosConta'
import { isShowDirectoryPickerAvailable } from './financeiroComissoesImport'

const MONTH_LABELS: Record<string, string> = {
  '01': 'Janeiro',
  '02': 'Fevereiro',
  '03': 'Março',
  '04': 'Abril',
  '05': 'Maio',
  '06': 'Junho',
  '07': 'Julho',
  '08': 'Agosto',
  '09': 'Setembro',
  '10': 'Outubro',
  '11': 'Novembro',
  '12': 'Dezembro',
}

const PDF_EXT = /\.pdf$/i

type AnyDirHandle = {
  kind: 'directory'
  name: string
  entries: () => AsyncIterableIterator<[string, AnyFsHandle]>
}

type AnyFileHandle = {
  kind: 'file'
  name: string
  getFile: () => Promise<File>
}

type AnyFsHandle = AnyDirHandle | AnyFileHandle

/** Extrai o ano removendo caracteres não numéricos. Ex.: "- 2026" → "2026". */
export function extractYearFromRootName(name: string): string | null {
  const digits = name.replace(/\D/g, '')
  if (digits.length === 4) return digits
  if (digits.length > 4) {
    const match = /(\d{4})/.exec(digits)
    return match ? match[1] : null
  }
  return null
}

/**
 * Mês pelos 1 ou 2 dígitos iniciais do nome da pasta, normalizado com 2 dígitos.
 * Ex.: "01 - JANEIRO" → "01", "1 - JANEIRO" → "01", "12 - DEZEMBRO" → "12".
 */
export function extractMonthFromFolderName(name: string): string | null {
  const match = /^\s*(\d{1,2})(?!\d)/.exec(name)
  if (!match) return null
  const n = Number(match[1])
  if (n < 1 || n > 12) return null
  return String(n).padStart(2, '0')
}

/** Dia se o nome for só número 1–31. Ex.: "23" → "23", "02" → "02". */
export function extractDayFromFolderName(name: string): string | null {
  const trimmed = name.trim()
  if (!/^\d{1,2}$/.test(trimmed)) return null
  const n = Number(trimmed)
  if (n < 1 || n > 31) return null
  return String(n).padStart(2, '0')
}

/** Conta a partir do nome do PDF. Ex.: "462209.pdf" → "462209". */
export function extractAccountFromPdfName(fileName: string): string | null {
  if (!PDF_EXT.test(fileName)) return null
  const base = fileName.replace(PDF_EXT, '')
  if (!/^\d+$/.test(base)) return null
  return base
}

export function monthLabel(month: string): string {
  return MONTH_LABELS[month] ?? month
}

function sortDays(days: FinanceiroExtratoContaDay[]): FinanceiroExtratoContaDay[] {
  return [...days].sort((a, b) => a.day.localeCompare(b.day))
}

function sortMonths(months: FinanceiroExtratoContaMonth[]): FinanceiroExtratoContaMonth[] {
  return [...months].sort((a, b) => a.month.localeCompare(b.month))
}

function buildMonthFromDays(
  month: string,
  folderName: string,
  days: FinanceiroExtratoContaDay[]
): FinanceiroExtratoContaMonth {
  const sortedDays = sortDays(days)
  const pdfCount = sortedDays.reduce((acc, d) => acc + d.files.length, 0)
  return {
    month,
    folderName,
    label: monthLabel(month),
    days: sortedDays,
    pdfCount,
  }
}

async function parseDayFolder(
  dir: AnyDirHandle,
  day: string
): Promise<{ dayNode: FinanceiroExtratoContaDay; skippedPdf: number }> {
  const files: FinanceiroExtratoContaFile[] = []
  let skippedPdf = 0

  for await (const [name, handle] of dir.entries()) {
    if (handle.kind !== 'file') continue
    const account = extractAccountFromPdfName(name)
    if (!account) {
      if (PDF_EXT.test(name)) skippedPdf += 1
      continue
    }
    try {
      const file = await handle.getFile()
      files.push({ account, fileName: name, file })
    } catch {
      /* ignore */
    }
  }

  files.sort((a, b) => a.account.localeCompare(b.account, undefined, { numeric: true }))

  return {
    dayNode: { day, folderName: dir.name, files },
    skippedPdf,
  }
}

async function parseMonthFolder(
  dir: AnyDirHandle,
  month: string
): Promise<{ monthNode: FinanceiroExtratoContaMonth; skippedFolders: number; skippedPdf: number }> {
  const days: FinanceiroExtratoContaDay[] = []
  let skippedFolders = 0
  let skippedPdf = 0

  for await (const [name, handle] of dir.entries()) {
    if (handle.kind !== 'directory') continue
    const day = extractDayFromFolderName(name)
    if (!day) {
      skippedFolders += 1
      continue
    }
    const parsed = await parseDayFolder(handle, day)
    days.push(parsed.dayNode)
    skippedPdf += parsed.skippedPdf
  }

  return {
    monthNode: buildMonthFromDays(month, dir.name, days),
    skippedFolders,
    skippedPdf,
  }
}

/**
 * Abre o seletor da pasta do ano e monta a árvore mês → dia → conta (PDF).
 */
export async function pickExtratosContaYearFolder(): Promise<FinanceiroExtratoContaTree | null> {
  if (!isShowDirectoryPickerAvailable()) return null

  const showDirectoryPicker = (
    window as unknown as {
      showDirectoryPicker: (opts?: { mode?: string }) => Promise<AnyDirHandle>
    }
  ).showDirectoryPicker

  let root: AnyDirHandle
  try {
    root = await showDirectoryPicker({ mode: 'read' })
  } catch (err) {
    if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'AbortError') {
      return null
    }
    throw err
  }

  const year = extractYearFromRootName(root.name)
  if (!year) {
    throw new Error(
      'Não foi possível identificar o ano na pasta selecionada. Selecione a pasta do ano (ex.: "- 2026").'
    )
  }

  const months: FinanceiroExtratoContaMonth[] = []
  let skippedFolderCount = 0
  let skippedPdfCount = 0

  for await (const [name, handle] of root.entries()) {
    if (handle.kind !== 'directory') continue
    const month = extractMonthFromFolderName(name)
    if (!month) {
      skippedFolderCount += 1
      continue
    }
    const parsed = await parseMonthFolder(handle, month)
    months.push(parsed.monthNode)
    skippedFolderCount += parsed.skippedFolders
    skippedPdfCount += parsed.skippedPdf
  }

  return {
    rootName: root.name,
    year,
    months: sortMonths(months),
    skippedFolderCount,
    skippedPdfCount,
  }
}

/**
 * Fallback webkitdirectory: monta a árvore a partir dos caminhos relativos.
 * Esperado: root / mês / dia / conta.pdf
 */
export function treeFromWebkitFiles(files: FileList | File[]): FinanceiroExtratoContaTree | null {
  const list = Array.from(files)
  if (list.length === 0) return null

  const first = list[0] as File & { webkitRelativePath?: string }
  const relative0 = first.webkitRelativePath || first.name
  const parts0 = relative0.split(/[/\\]/).filter(Boolean)
  if (parts0.length < 2) return null

  const rootName = parts0[0]
  const year = extractYearFromRootName(rootName)
  if (!year) {
    throw new Error(
      'Não foi possível identificar o ano na pasta selecionada. Selecione a pasta do ano (ex.: "- 2026").'
    )
  }

  type DayAcc = Map<string, { folderName: string; files: FinanceiroExtratoContaFile[] }>
  type MonthAcc = {
    folderName: string
    days: DayAcc
  }
  const monthMap = new Map<string, MonthAcc>()
  let skippedFolderCount = 0
  let skippedPdfCount = 0
  const seenNonDayFolders = new Set<string>()

  for (const file of list) {
    const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
    const parts = relative.split(/[/\\]/).filter(Boolean)
    // root / month / day / file
    if (parts.length < 4) continue

    const monthFolder = parts[1]
    const dayFolder = parts[2]
    const fileName = parts[parts.length - 1]

    const month = extractMonthFromFolderName(monthFolder)
    if (!month) continue

    const day = extractDayFromFolderName(dayFolder)
    if (!day) {
      const key = `${monthFolder}/${dayFolder}`
      if (!seenNonDayFolders.has(key)) {
        seenNonDayFolders.add(key)
        skippedFolderCount += 1
      }
      continue
    }

    let monthAcc = monthMap.get(month)
    if (!monthAcc) {
      monthAcc = { folderName: monthFolder, days: new Map() }
      monthMap.set(month, monthAcc)
    }

    let dayAcc = monthAcc.days.get(day)
    if (!dayAcc) {
      dayAcc = { folderName: dayFolder, files: [] }
      monthAcc.days.set(day, dayAcc)
    }

    const account = extractAccountFromPdfName(fileName)
    if (!account) {
      if (PDF_EXT.test(fileName)) skippedPdfCount += 1
      continue
    }

    dayAcc.files.push({ account, fileName, file })
  }

  const months: FinanceiroExtratoContaMonth[] = []
  for (const [month, acc] of monthMap) {
    const days: FinanceiroExtratoContaDay[] = []
    for (const [day, dayAcc] of acc.days) {
      const files = [...dayAcc.files].sort((a, b) =>
        a.account.localeCompare(b.account, undefined, { numeric: true })
      )
      days.push({ day, folderName: dayAcc.folderName, files })
    }
    months.push(buildMonthFromDays(month, acc.folderName, days))
  }

  if (months.length === 0) return null

  return {
    rootName,
    year,
    months: sortMonths(months),
    skippedFolderCount,
    skippedPdfCount,
  }
}

export type ExtratosContaScopeSelection = {
  /** Meses selecionados (ex.: "01"). Vazio = todos. */
  months: string[]
  /** Chaves "MM-DD". Vazio = todos os dias do recorte de meses. */
  days: string[]
  /** Números de conta. Vazio = todas as contas do recorte. */
  accounts: string[]
}

export function dayKey(month: string, day: string): string {
  return `${month}-${day}`
}

/** Achata a árvore aplicando o filtro de escopo. */
export function flattenScope(
  tree: FinanceiroExtratoContaTree,
  selection: ExtratosContaScopeSelection
): FinanceiroExtratoContaScopeRow[] {
  const monthFilter =
    selection.months.length > 0 ? new Set(selection.months) : null
  const dayFilter = selection.days.length > 0 ? new Set(selection.days) : null
  const accountFilter =
    selection.accounts.length > 0 ? new Set(selection.accounts) : null

  const rows: FinanceiroExtratoContaScopeRow[] = []

  for (const m of tree.months) {
    if (monthFilter && !monthFilter.has(m.month)) continue
    for (const d of m.days) {
      const dk = dayKey(m.month, d.day)
      if (dayFilter && !dayFilter.has(dk)) continue
      for (const f of d.files) {
        if (accountFilter && !accountFilter.has(f.account)) continue
        rows.push({
          month: m.month,
          monthLabel: m.label,
          day: d.day,
          account: f.account,
          fileName: f.fileName,
          file: f.file,
        })
      }
    }
  }

  return rows
}

/** Contas distintas no recorte mês/dia (antes do filtro de conta). */
export function distinctAccountsInMonthDayScope(
  tree: FinanceiroExtratoContaTree,
  months: string[],
  days: string[]
): string[] {
  const monthFilter = months.length > 0 ? new Set(months) : null
  const dayFilter = days.length > 0 ? new Set(days) : null
  const set = new Set<string>()

  for (const m of tree.months) {
    if (monthFilter && !monthFilter.has(m.month)) continue
    for (const d of m.days) {
      const dk = dayKey(m.month, d.day)
      if (dayFilter && !dayFilter.has(dk)) continue
      for (const f of d.files) set.add(f.account)
    }
  }

  return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/** Dias disponíveis no recorte de meses. */
export function daysInMonthScope(
  tree: FinanceiroExtratoContaTree,
  months: string[]
): Array<{ month: string; monthLabel: string; day: string; key: string; pdfCount: number }> {
  const monthFilter = months.length > 0 ? new Set(months) : null
  const out: Array<{
    month: string
    monthLabel: string
    day: string
    key: string
    pdfCount: number
  }> = []

  for (const m of tree.months) {
    if (monthFilter && !monthFilter.has(m.month)) continue
    for (const d of m.days) {
      out.push({
        month: m.month,
        monthLabel: m.label,
        day: d.day,
        key: dayKey(m.month, d.day),
        pdfCount: d.files.length,
      })
    }
  }

  return out
}

export { isShowDirectoryPickerAvailable }

export const PEL_WEBHOOK_URL = 'https://webhook.pasiai.com/webhook/PeL'

export type SendPelExtratoPdfParams = {
  file: File
  folderId: string
  fileId: string
  year: string
  month: string
  day: string
  /** Nome do arquivo PDF (ex.: 462209.pdf). */
  fileName: string
  /** Número da conta: só dígitos do nome do arquivo. */
  account: string
}

/**
 * Envia um PDF ao webhook PeL com folder_id, file_id, data, nome do arquivo e conta.
 * Aguarda a resposta antes de resolver.
 */
export async function sendPelExtratoPdf(params: SendPelExtratoPdfParams): Promise<void> {
  const formData = new FormData()
  formData.append('arquivo', params.file)
  formData.append('folder_id', params.folderId)
  formData.append('file_id', params.fileId)
  formData.append('data', `${params.day}/${params.month}/${params.year}`)
  formData.append('nome_arquivo', params.fileName)
  formData.append('conta', params.account.replace(/\D/g, ''))

  const response = await fetch(PEL_WEBHOOK_URL, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`Webhook PeL retornou HTTP ${response.status}`)
  }

  let json: unknown
  try {
    json = await response.json()
  } catch {
    throw new Error('Webhook PeL retornou resposta sem JSON válido.')
  }

  // Sucesso esperado: [{ "sucesso": true }]
  const first = Array.isArray(json) ? json[0] : null
  const raw =
    first && typeof first === 'object' && first !== null && 'sucesso' in first
      ? (first as { sucesso: unknown }).sucesso
      : undefined

  const isSuccess =
    raw === true || (typeof raw === 'string' && raw.trim().toLowerCase() === 'true')

  if (!isSuccess) {
    throw new Error(
      raw !== undefined && raw !== null
        ? `Webhook PeL não retornou sucesso (sucesso=${String(raw)}).`
        : 'Webhook PeL não retornou sucesso.'
    )
  }
}
